import * as React from 'react'
import moment from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  CollapsibleSidePanel,
  SubNavigationBar,
  SubNavigationItem,
} from '../shared/components'
import { ExportIcon } from '@/app/features/icons/Export'
import { REPORT_ROUTES } from './routes-typed'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { HeadBar } from './components/HeadBar'
import { useExport } from './stores/exportStore'
import { DatePickerRangeProps } from '@/modules/drivers/types'
import clx from 'classnames'
import PrintoutDriversTable from '@/modules/printouts/components/PrintoutDriversTable'
import { TableProvider } from '@/modules/drivers/context/TableContext'
import { CompanyNameBadge } from '@/modules/drivers/shared/viewBars/CompanyNameBadge'
import { useImiFavorites } from './services/useImiFavoritesService'
import { useImiSidePanel } from './context/ImiSidePanelContext'

type Props = {
  children: React.ReactNode
  companyId: string
  viewControlBar?: () => React.JSX.Element
  heightScreen?: boolean
  fullScreen?: boolean
  hideSidePanel?: boolean
}

export const ReportsLayout = ({
  children,
  companyId,
  viewControlBar: ViewControlBar,
  heightScreen = false,
  fullScreen = false,
  hideSidePanel = false,
}: Props) => {
  const { t } = useLocalize()
  const { setOutputType, outputType, dateRange, setDateRange, syncDateRange } = useExport()
  const { month, range, setMonth } = useDate()

  // The export store survives navigation; refresh its initial range on entry
  // and whenever the shared accounting period changes.
  React.useLayoutEffect(() => {
    syncDateRange()
  }, [month, range, syncDateRange])

  const { favorites, toggleFavorite } = useImiFavorites(companyId)
  const {
    selectedDriverIds,
    setSelectedDriverIds,
    driverTableKey,
    bumpDriverTableKey,
  } = useImiSidePanel()

  const handleSelectionChange = React.useCallback(
    (ids: number[]) => {
      setSelectedDriverIds(ids.map(String))
    },
    [setSelectedDriverIds],
  )

  const prevCompanyRef = React.useRef(companyId)
  React.useEffect(() => {
    if (prevCompanyRef.current === companyId) return
    prevCompanyRef.current = companyId
    setSelectedDriverIds([])
    bumpDriverTableKey()
  }, [companyId, setSelectedDriverIds, bumpDriverTableKey])

  const handleDateRangeChange = React.useCallback(
    (newRange: DatePickerRangeProps) => {
      setDateRange(newRange)
    },
    [setDateRange],
  )

  React.useEffect(() => {
    if (!month) {
      const currentMonth = moment().format('YYYY-MM')
      setMonth(currentMonth)
    }
  }, [month, setMonth])
  const baseClasses = clx(
    'module-reports tw-flex tw-flex-[1] tw-text-content-base tw-bg-base-100 tw-min-w-0 tw-max-w-full tw-overflow-hidden',
    {
      'tw-h-screen': fullScreen,
      'tw-padding-4 tw-mt-1 tw-mb-1 tw-rounded-xl tw-border tw-border-base-300':
        !fullScreen,
      'tw-h-[calc(100vh-8px)]': !fullScreen,
    },
  )
  const boundaryRef = React.useRef<HTMLDivElement>(null)
  return (
    <div className={baseClasses} ref={boundaryRef}>
   
      {!hideSidePanel && (
        <CollapsibleSidePanel
          className="tw-flex tw-min-w-[350px] tw-flex-shrink-0 tw-flex-col tw-overflow-hidden tw-border-r tw-border-base-300 tw-bg-base-100"
          drawerClassName="tw-w-[350px] tw-max-w-[85vw]"
        >
          <div className="tw-p-2 tw-pt-3.5">
            <CompanyNameBadge fullWidth />
          </div>
          <TableProvider
            key={`${companyId}-${driverTableKey}`}
            defaultSelected={selectedDriverIds
              .map(Number)
              .filter((id) => !Number.isNaN(id))}
          >
            <PrintoutDriversTable
              companyId={companyId}
              onSelectionChange={handleSelectionChange}
              favoriteDriverIds={favorites.drivers}
              onToggleFavorite={(id) => toggleFavorite('drivers', id)}
            />
          </TableProvider>
        </CollapsibleSidePanel>
      )}

      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-x-auto tw-overflow-y-hidden">
      <div className="tw-flex tw-min-h-0 tw-min-w-[900px] tw-flex-1 tw-flex-col tw-overflow-hidden">
        <HeadBar
          title={t('dashboard.export')}
          icon={<ExportIcon />}
          setOutputType={setOutputType}
          outputType={outputType}
          handleDateRangeChange={handleDateRangeChange}
          range={dateRange}
        />

        <SubNavigationBar>
          <SubNavigationItem
            to={REPORT_ROUTES.reportsList.buildPath({ companyId: companyId })}
          >
            {t('menu.export-list')}
          </SubNavigationItem>
          <SubNavigationItem
            to={REPORT_ROUTES.imiDeclarations.buildPath({
              companyId: companyId,
            })}
          >
            {t('exportModule.manage-declarations')}
          </SubNavigationItem>
        </SubNavigationBar>

        {ViewControlBar && <ViewControlBar />}

        <main
          className={`tw-flex tw-h-full tw-flex-1 ${heightScreen ? 'tw-min-h-0 tw-max-h-screen tw-flex-col tw-overflow-hidden' : ''} `}
        >
          {children}
        </main>
      </div>
      </div>
    </div>
  )
}
